import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertCard } from './ui/card-spotlight';
import { StatusBadge } from './StatusBadge';
import { ExternalLink, Mail, Eye, XCircle, Users } from 'lucide-react';

// Group issue backlinks by provider for the "Contact All" feature
function groupByProvider(bls) {
  const map = new Map();
  for (const bl of bls) {
    const key = bl.provider_id || '__none__';
    if (!map.has(key)) {
      map.set(key, {
        provider_name: bl.provider_name || 'Unassigned',
        contact_email: bl.provider_contact_email || null,
        backlinks: [],
      });
    }
    map.get(key).backlinks.push(bl);
  }
  return [...map.values()];
}

// Build mailto link for contacting a provider about issues
function buildMailtoLink(providerEmail, providerName, issueBacklinks, issueType) {
  if (!providerEmail) return null;
  const subject = encodeURIComponent(`Backlink issue: ${issueType} — ${providerName}`);
  const urls = issueBacklinks.map(bl => `  - ${bl.backlink_url} (${bl.status})`).join('\n');
  const body = encodeURIComponent(
    `Hi ${providerName},\n\nWe've detected the following backlink issues:\n\n${urls}\n\nPlease investigate and advise on resolution.\n\nThank you`
  );
  return `mailto:${providerEmail}?subject=${subject}&body=${body}`;
}

export function NeedsAttention({ backlinks = [], alerts = [], onDismiss, className }) {
  // Group backlinks by issue type
  const issues = useMemo(() => {
    const grouped = {
      lost: [],
      nofollow: [],
      redirect: [],
      error: [],
    };

    backlinks.forEach((bl) => {
      if (bl.status === 'removed') {
        grouped.lost.push(bl);
      } else if (bl.status === 'nofollow_changed') {
        grouped.nofollow.push(bl);
      } else if (bl.status === 'redirected') {
        grouped.redirect.push(bl);
      } else if (['404', '5xx', 'timeout'].includes(bl.status)) {
        grouped.error.push(bl);
      }
    });

    return grouped;
  }, [backlinks]);

  // Group all issue backlinks by provider
  const providerGroups = useMemo(() => {
    const allIssues = [...issues.lost, ...issues.nofollow, ...issues.redirect, ...issues.error];
    return groupByProvider(allIssues);
  }, [issues]);

  const hasIssues =
    issues.lost.length > 0 ||
    issues.nofollow.length > 0 ||
    issues.redirect.length > 0 ||
    issues.error.length > 0;

  if (!hasIssues && alerts.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className={`rounded-xl border border-success/20 bg-success/5 p-6 text-center ${className}`}
      >
        <div className="text-success text-4xl mb-3">✓</div>
        <h3 className="text-lg font-semibold text-foreground mb-1">All Clear!</h3>
        <p className="text-sm text-muted-foreground">
          No issues detected. All your backlinks are healthy.
        </p>
      </motion.div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground">Needs Attention</h2>
        <span className="text-xs text-muted-foreground">
          {issues.lost.length + issues.nofollow.length + issues.redirect.length + issues.error.length} issues
        </span>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {/* Lost Links */}
        {issues.lost.length > 0 && (
          <AlertCard
            type="critical"
            title="Lost Links"
            description="These backlinks are no longer found on the source pages"
            hint="Contact provider for reinstatement or credit"
            count={issues.lost.length}
            items={issues.lost.slice(0, 3).map((bl) => ({
              label: new URL(bl.backlink_url).hostname,
              action: {
                label: 'View',
                onClick: () => {},
              },
            }))}
            actions={
              <div className="flex gap-2">
                <ActionButton icon={<Mail className="h-3.5 w-3.5" />} label="Contact All" />
                <ActionButton icon={<Eye className="h-3.5 w-3.5" />} label="View All" variant="secondary" />
              </div>
            }
          />
        )}

        {/* Nofollow Changed */}
        {issues.nofollow.length > 0 && (
          <AlertCard
            type="warning"
            title="Nofollow Changed"
            description="These links have been changed to nofollow"
            hint="Ask provider for dofollow replacement"
            count={issues.nofollow.length}
            items={issues.nofollow.slice(0, 3).map((bl) => ({
              label: new URL(bl.backlink_url).hostname,
              action: {
                label: 'View',
                onClick: () => {},
              },
            }))}
            actions={
              <div className="flex gap-2">
                <ActionButton icon={<Mail className="h-3.5 w-3.5" />} label="Contact" />
                <ActionButton icon={<XCircle className="h-3.5 w-3.5" />} label="Dismiss" variant="ghost" />
              </div>
            }
          />
        )}

        {/* Redirected */}
        {issues.redirect.length > 0 && (
          <AlertCard
            type="warning"
            title="Redirected"
            description="Source pages are redirecting"
            hint="Check if final URL still contains your link"
            count={issues.redirect.length}
            items={issues.redirect.slice(0, 3).map((bl) => ({
              label: new URL(bl.backlink_url).hostname,
            }))}
            actions={
              <ActionButton icon={<Eye className="h-3.5 w-3.5" />} label="Review" />
            }
          />
        )}

        {/* Errors */}
        {issues.error.length > 0 && (
          <AlertCard
            type="critical"
            title="Page Errors"
            description="Source pages returning errors"
            hint="Watch 48h — may be transient. Remove persistent 404s"
            count={issues.error.length}
            items={issues.error.slice(0, 3).map((bl) => ({
              label: `${new URL(bl.backlink_url).hostname} (${bl.status})`,
            }))}
            actions={
              <ActionButton icon={<Eye className="h-3.5 w-3.5" />} label="Review" />
            }
          />
        )}
      </div>

      {/* Provider Breakdown */}
      {providerGroups.length > 1 && (
        <div className="mt-4">
          <h3 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-1.5">
            <Users className="h-3.5 w-3.5" />
            Issues by Provider
          </h3>
          <div className="flex flex-wrap gap-2">
            {providerGroups.map((pg) => {
              const mailto = buildMailtoLink(pg.contact_email, pg.provider_name, pg.backlinks, 'Multiple issues');
              return (
                <div key={pg.provider_name} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-card/50 border border-border/50 text-sm">
                  <span className="font-medium text-foreground">{pg.provider_name}</span>
                  <span className="text-destructive font-medium">{pg.backlinks.length}</span>
                  {mailto && (
                    <a
                      href={mailto}
                      className="ml-1 text-primary hover:text-primary/80 text-xs font-medium"
                    >
                      Contact
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recent Alerts */}
      {alerts.length > 0 && (
        <div className="mt-6">
          <h3 className="text-sm font-medium text-muted-foreground mb-3">Recent Alerts</h3>
          <div className="space-y-2">
            {alerts.slice(0, 5).map((alert) => (
              <motion.div
                key={alert.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                className="flex items-center justify-between p-3 rounded-lg bg-card/50 border border-border/50"
              >
                <div className="flex items-center gap-3">
                  <StatusBadge status={alert.type === 'link_removed' ? 'removed' : 'nofollow_changed'} showLabel={false} />
                  <div>
                    <p className="text-sm text-foreground">{getAlertMessage(alert)}</p>
                    <p className="text-xs text-muted-foreground">{formatTime(alert.created_at)}</p>
                  </div>
                </div>
                <Link
                  to={`/backlinks/${alert.backlink_id}`}
                  className="text-primary hover:text-primary/80 text-sm"
                >
                  <ExternalLink className="h-4 w-4" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ActionButton({ icon, label, variant = 'primary', onClick }) {
  const variants = {
    primary: 'bg-primary hover:bg-primary/90 text-primary-foreground',
    secondary: 'bg-muted hover:bg-muted/80 text-foreground',
    ghost: 'hover:bg-muted text-muted-foreground hover:text-foreground',
  };

  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${variants[variant]}`}
    >
      {icon}
      {label}
    </button>
  );
}

function getAlertMessage(alert) {
  const messages = {
    link_removed: 'Link removed from page',
    nofollow_added: 'Link changed to nofollow',
    redirect: 'Page redirecting',
    page_404: 'Page returning 404',
    anchor_changed: 'Anchor text changed',
    da_drop: 'Domain Authority dropped',
    link_restored: 'Link restored',
  };
  return messages[alert.type] || alert.type;
}

function formatTime(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diff = now - date;
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  return 'Just now';
}

export default NeedsAttention;
